import { CurrencyPipe, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import {
  faArrowLeft,
  faArrowRight,
  faChevronRight,
  faExclamationTriangle,
  faEye,
  faRotateLeft,
} from '@fortawesome/pro-regular-svg-icons';
import { CreditTransactionType, PayoutType, Type } from '@enrollhere/classes';
import { AccountCardComponent } from '../../account/account-card/account-card.component';
import { WalletKindBadgeComponent } from '../../account/wallet-kind-badge/wallet-kind-badge.component';
import { UserCardComponent } from '../../ui-components/user-card/user-card.component';
import {
  ModalContainerService,
  ShowModalComponents,
} from '../../../services/modal-container.service';
import {
  creditTransactionTypeMap,
  payoutTypeMap,
  typeMap,
} from '../../../data/payoutType';
import {
  WalletLike,
  getFundingSource,
  getTransactionDirection,
  getWalletKind,
  transactionDirectionMap,
  walletKindMap,
} from '../../../data/walletKind';
import { environment } from '../../../../../environments/environment';
import { EnrichedPayout } from '../../../services/payout-api.service';

/** Data state passed to the payouts table */
export interface PayoutsTableDataState {
  loading: boolean;
  payouts: EnrichedPayout[] | null;
  payout: EnrichedPayout | null;
  error: string | null;
}

@Component({
  selector: 'app-payouts-table',
  standalone: true,
  imports: [
    AccountCardComponent,
    CurrencyPipe,
    DatePipe,
    FaIconComponent,
    UserCardComponent,
    WalletKindBadgeComponent,
  ],
  templateUrl: './payouts-table.component.html',
  styleUrl: './payouts-table.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class PayoutsTableComponent {
  private modalContainerService = inject(ModalContainerService);

  faArrowLeft = faArrowLeft;
  faArrowRight = faArrowRight;
  faChevronRight = faChevronRight;
  faExclamationTriangle = faExclamationTriangle;
  faEye = faEye;
  faRotateLeft = faRotateLeft;

  creditTransactionTypeMap = creditTransactionTypeMap;
  payoutTypeMap = payoutTypeMap;
  typeMap = typeMap;

  CreditTransactionType = CreditTransactionType;
  PayoutType = PayoutType;
  Type = Type;

  // Signal inputs
  data = input<PayoutsTableDataState>();
  userId = input<string>();

  // Signal-based view children
  table = viewChild<ElementRef>('table');
  container = viewChild<ElementRef>('container');

  // Signal for arrow visibility
  showArrows = signal(false);

  constructor() {
    // Effect to calculate arrow visibility when payouts data changes
    effect(() => {
      const data = this.data();
      const container = this.container();
      const table = this.table();

      if (data?.payouts && container && table) {
        // Use setTimeout to ensure DOM has rendered
        setTimeout(() => {
          const containerWidth = container.nativeElement.offsetWidth;
          const tableWidth = table.nativeElement.scrollWidth;
          this.showArrows.set(tableWidth > containerWidth);
        }, 10);
      }
    });
  }

  /**
   * A card top-up that was refunded back off the wallet is stored as a
   * top-up row with a negative amount (AE-5198 admin tool, AE-5642 agency
   * refunds); labelling it "Add Funds" would read as the opposite of what
   * happened.
   */
  isTopUpRefund(payout: EnrichedPayout): boolean {
    const isTopUp =
      payout.payoutType === PayoutType.PT_TOP_UP ||
      payout.payoutType === PayoutType.PT_AUTO_TOP_UP;
    return isTopUp && (payout.transaction?.amount ?? 0) < 0;
  }

  scrollLeft() {
    const container = this.container();
    if (container) {
      container.nativeElement.scrollTo({
        left: container.nativeElement.scrollLeft - 250,
        behavior: 'smooth',
      });
    }
  }

  scrollRight() {
    const container = this.container();
    if (container) {
      container.nativeElement.scrollTo({
        left: container.nativeElement.scrollLeft + 250,
        behavior: 'smooth',
      });
    }
  }

  /** Where the money came from / which pool it was taken from */
  fundingSource(payment: EnrichedPayout) {
    return getFundingSource(payment);
  }

  /** Personal / Team / Agency title for the counterpart wallet, if known */
  walletKindTitle(account?: WalletLike): string | undefined {
    const kind = getWalletKind(account);
    return kind ? walletKindMap[kind].title : undefined;
  }

  /** Green for additions, red for subtractions */
  directionClass(payment: EnrichedPayout): string {
    const direction = getTransactionDirection(payment);
    return direction ? transactionDirectionMap[direction].textClass : '';
  }

  showPayment(payment: EnrichedPayout) {
    const userId = this.userId();
    if (!userId) {
      console.error('userId missing');
      return;
    }

    this.modalContainerService.open(ShowModalComponents.PAYOUT, {
      payment,
      userId,
    });
  }

  openSource(payment: EnrichedPayout) {
    switch (payment.source?.type) {
      case Type.T_DIALER_CALL:
        window.open(
          `${environment.enrollhereDialerUrl}/lead/${payment.source.agency?.id}/${payment.source?.lead?.id}/${payment.source?.id}`,
          '_blank'
        );
        break;
    }
  }
}
